import React from 'react';
import { View, XStack, YStack } from 'tamagui';

import { isEmpty } from '../../misc';
import type { ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { Button, ButtonSize, ButtonVariant } from '../buttons/Button';
import { Icon, type IconType } from '../icons/Icon';
import { Shadows } from '../Shadows';
import { BodyLargeEmphasized, BodyMediumRegular } from '../text/Typography';

interface ActionProps {
    text: string;
    onPress: () => void;
    disabled?: boolean;
    icon?: IconType;
}

interface NextActionProps {
    title: string;
    description?: string;
    icon?: IconType;
    iconOverride?: React.ReactElement;
    primaryAction: ActionProps;
    secondaryAction?: ActionProps;
    backgroundColor?: ColorTokenKey;
    shadow?: boolean;
    maxWidth?: number;
}

export const NextAction = ({
    title,
    description,
    icon,
    iconOverride,
    primaryAction,
    secondaryAction,
    backgroundColor = '$gray200',
    shadow = false,
    maxWidth,
}: NextActionProps) => {
    return (
        <YStack
            width="100%"
            minWidth={330}
            padding={16}
            gap={16}
            borderRadius={20}
            backgroundColor={backgroundColor}
            maxWidth={maxWidth}
            alignSelf="center"
            {...(shadow ? Shadows.MEDIUM : {})}
            $mobile_mini={{
                minWidth: 300,
                paddingHorizontal: 12,
            }}
        >
            <XStack gap={12} alignItems="flex-start">
                {iconOverride != null ? (
                    <>{iconOverride}</>
                ) : icon != null ? (
                    <View
                        backgroundColor="$gray300"
                        padding={8}
                        borderRadius={12}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon icon={icon} size={24} color="$gray1200" />
                    </View>
                ) : null}
                <YStack flex={1} gap={4}>
                    <BodyLargeEmphasized color="$black">
                        {title}
                    </BodyLargeEmphasized>
                    {!isEmpty(description) && (
                        <BodyMediumRegular color="$gray1100">
                            {description}
                        </BodyMediumRegular>
                    )}
                </YStack>
            </XStack>

            <XStack gap={12}>
                {secondaryAction != null && (
                    <Button
                        buttonProps={{ flex: 1 }}
                        text={secondaryAction.text}
                        variant={ButtonVariant.SECONDARY} // TODO: Update when we update buttons
                        size={ButtonSize.LARGE}
                        onPress={secondaryAction.onPress}
                        disabled={secondaryAction.disabled}
                        icon={secondaryAction.icon}
                    />
                )}
                <Button
                    buttonProps={{ flex: 1 }}
                    text={primaryAction.text}
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={primaryAction.onPress}
                    disabled={primaryAction.disabled}
                    icon={primaryAction.icon}
                />
            </XStack>
        </YStack>
    );
};
